import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShoppingBag, Recycle, Coins, ChevronDown, Star } from "lucide-react";
import api from "../lib/api";
import { Reveal, MaskedLine, Overline } from "../components/Reveal";
import ProductCard from "../components/ProductCard";

const IMGS = {
  hero1: "https://images.unsplash.com/photo-1606143412458-acc5f86de897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxlZGl0b3JpYWwlMjBmYXNoaW9uJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg2Njg4NTI2fDA&ixlib=rb-4.1.0&q=85",
  hero2: "https://images.unsplash.com/photo-1601597565151-70c4020dc0e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxlZGl0b3JpYWwlMjBmYXNoaW9uJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg2Njg4NTI2fDA&ixlib=rb-4.1.0&q=85",
  men: "https://images.unsplash.com/photo-1741601466156-82ff4dcc4225?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxtZW4lMjBoaWdoJTIwZmFzaGlvbiUyMHN0dWRpb3xlbnwwfHx8fDE3ODY2ODg1MjZ8MA&ixlib=rb-4.1.0&q=85",
  kids: "https://images.unsplash.com/photo-1758782213532-bbb5fd89885e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxraWRzJTIwZmFzaGlvbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NjY4ODU0M3ww&ixlib=rb-4.1.0&q=85",
  tradein: "https://images.unsplash.com/photo-1604176354204-9268737828e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwZm9sZGVkJTIwY2xvdGhlc3xlbnwwfHx8fDE3ODY2ODg1Mjd8MA&ixlib=rb-4.1.0&q=85",
};

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -70]);
  return (
    <section ref={ref} className="relative min-h-[82svh] flex items-center overflow-hidden" data-testid="hero">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 w-full grid lg:grid-cols-2 gap-12 items-center pt-14 pb-12 lg:pt-0">
        <div className="relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
            className="text-[11px] tracking-[0.35em] uppercase text-muted-foreground mb-7">
            JIGEL · Premium Clothing Recommerce
          </motion.p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.94] text-ink">
            <MaskedLine delay={0.15}>Premium Fashion.</MaskedLine>
            <MaskedLine delay={0.35} className="italic font-light text-wine">Give It Another Life.</MaskedLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-7 max-w-md text-base lg:text-lg leading-relaxed text-ink/60">
            Shop carefully selected pre-owned fashion or sell your unwanted clothes to JIGEL.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-9 flex flex-wrap gap-4">
            <Link to="/shop" data-testid="hero-shop-btn"
              className="group bg-ink text-ivory px-9 py-4 text-[11px] tracking-[0.25em] uppercase flex items-center gap-3 hover:bg-wine transition-colors duration-300">
              Shop Now <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/sell" data-testid="hero-sell-btn"
              className="group bg-gold text-ink px-8 py-4 text-[11px] tracking-[0.25em] uppercase flex items-center gap-3 hover:bg-ink hover:text-ivory transition-colors duration-300">
              Sell Your Clothes <Recycle size={14} strokeWidth={1.5} />
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
            className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-[11px] tracking-[0.15em] uppercase text-muted-foreground">
            <Link to="/men" data-testid="hero-men" className="hover:text-wine transition-colors">Men</Link>
            <Link to="/women" data-testid="hero-women" className="hover:text-wine transition-colors">Women</Link>
            <Link to="/kids" data-testid="hero-kids" className="hover:text-wine transition-colors">Kids</Link>
            <Link to="/brands" data-testid="hero-brands" className="hover:text-wine transition-colors">Brands</Link>
          </motion.div>
        </div>
        <div className="relative hidden lg:block h-[68vh]">
          <motion.div style={{ y: y1 }} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-0 w-[62%] aspect-[3/4] overflow-hidden">
            <img src={IMGS.hero1} alt="Premium fashion" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div style={{ y: y2 }} initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-0 bottom-0 w-[46%] aspect-[3/4] overflow-hidden border-[10px] border-ivory shadow-2xl">
            <img src={IMGS.hero2} alt="Second-life fashion" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ModelCards() {
  const cards = [
    { icon: ShoppingBag, t: "Shop", d: "Find premium second-life fashion — inspected, graded and honestly priced.", to: "/shop", cta: "Shop Now", testid: "model-shop" },
    { icon: Recycle, t: "Sell", d: "Turn unwanted clothes into Cash or Cloth Cash, depending on condition.", to: "/sell", cta: "Sell Your Clothes", testid: "model-sell" },
    { icon: Coins, t: "Cloth Cash", d: "Use your Cloth Cash on eligible JIGEL and AVAGINJA purchases.", to: "/cloth-cash", cta: "My Cloth Cash", testid: "model-clothcash" },
  ];
  return (
    <section className="max-w-[1600px] mx-auto px-5 md:px-10 -mt-2 pb-14" data-testid="model-cards">
      <div className="grid md:grid-cols-3 gap-4">
        {cards.map((c, i) => (
          <Reveal key={c.t} delay={i * 0.1}>
            <Link to={c.to} data-testid={c.testid}
              className="group block border border-border bg-ivory p-7 hover:border-wine transition-colors duration-300 h-full">
              <c.icon size={22} strokeWidth={1} className="text-wine" />
              <h3 className="font-serif text-3xl mt-4">{c.t}</h3>
              <p className="mt-2 text-sm text-ink/55 leading-relaxed">{c.d}</p>
              <p className="mt-4 text-[10px] tracking-[0.25em] uppercase text-wine flex items-center gap-2">{c.cta} <ArrowRight size={12} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" /></p>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Departments() {
  const deps = [
    { to: "/men", label: "Men", img: IMGS.men },
    { to: "/women", label: "Women", img: IMGS.hero1 },
    { to: "/kids", label: "Kids", img: IMGS.kids },
  ];
  return (
    <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-14 md:py-20" data-testid="departments">
      <Reveal className="flex items-end justify-between gap-6">
        <div>
          <Overline>Shop by Category</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Men · Women · Kids</h2>
        </div>
        <Link to="/shop" data-testid="dept-view-all" className="hidden md:flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-wine hover:gap-3 transition-all">
          Shop Everything <ArrowRight size={14} strokeWidth={1.5} />
        </Link>
      </Reveal>
      <div className="mt-10 grid md:grid-cols-3 gap-5">
        {deps.map((d, i) => (
          <Reveal key={d.to} delay={i * 0.1}>
            <Link to={d.to} data-testid={`dept-${d.label.toLowerCase()}`} className="group block relative overflow-hidden aspect-[4/5]">
              <img src={d.img} alt={d.label} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-ink/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
                <h3 className="font-serif text-2xl md:text-3xl text-ivory">Shop {d.label}</h3>
                <span className="w-10 h-10 bg-ivory text-ink flex items-center justify-center transition-all duration-300 group-hover:bg-gold">
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function BrandTiles({ brands }) {
  const featured = brands.filter((b) => b.featured);
  if (!featured.length) return null;
  return (
    <section className="bg-sand/60" data-testid="featured-brands">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-14 md:py-20">
        <Reveal className="flex items-end justify-between gap-6">
          <div>
            <Overline>Shop by Brand</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Featured brands.</h2>
          </div>
          <Link to="/brands" data-testid="view-all-brands" className="hidden md:flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-wine hover:gap-3 transition-all">
            A–Z Directory <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 border-t border-l border-border">
          {featured.slice(0, 8).map((b, i) => (
            <Link key={b.slug} to={`/brands/${b.slug}`} data-testid={`brand-card-${b.slug}`}
              className="group flex flex-col justify-between h-40 md:h-48 p-6 border-r border-b border-border hover:bg-ivory transition-colors duration-500">
              <span className="font-mono text-[10px] text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <p className="font-serif text-2xl md:text-3xl tracking-wide group-hover:text-wine transition-colors">{b.name}</p>
                <p className="mt-1 text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{b.product_count} pieces</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductRail({ title, overline, params, to }) {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products", { params }).then((r) => setProducts(r.data.slice(0, 8))).catch(() => {}); }, []);
  if (!products.length) return null;
  return (
    <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-14 md:py-20" data-testid={`rail-${overline.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <Reveal className="flex items-end justify-between gap-6">
        <div>
          <Overline>{overline}</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">{title}</h2>
        </div>
        <Link to={to} data-testid={`rail-view-all-${overline.toLowerCase().replace(/[^a-z]/g, "-")}`}
          className="hidden md:flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-wine hover:gap-3 transition-all">
          View All <ArrowRight size={14} strokeWidth={1.5} />
        </Link>
      </Reveal>
      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
        {products.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
      </div>
    </section>
  );
}

function SellSection() {
  return (
    <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-14 md:py-24" data-testid="sell-section">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal className="relative">
          <div className="aspect-[4/5] overflow-hidden">
            <img src={IMGS.tradein} alt="Clothes ready to sell" loading="lazy" className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <div>
          <Reveal>
            <Overline>Sell Your Old Clothes</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mt-5">Your clothes can give you <span className="italic font-light text-wine">more than cash.</span></h2>
            <p className="mt-7 text-ink/60 text-base lg:text-lg leading-relaxed max-w-md">
              Submit your clothes. We evaluate every item, and depending on its condition and resale potential you receive Cash or Cloth Cash.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-8 grid grid-cols-3 gap-3" data-testid="tiers-preview">
            {[["Reusable", "Cash"], ["Good", "Cloth Cash"], ["Recovery", "Recovery Cloth Cash"]].map(([t, r]) => (
              <div key={t} className="border border-border p-4 text-center">
                <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{t}</p>
                <p className="mt-2 font-serif text-lg text-wine">{r}</p>
              </div>
            ))}
          </Reveal>
          <Reveal delay={0.15} className="mt-9">
            <Link to="/sell" data-testid="sell-cta-btn"
              className="group inline-flex bg-wine text-ivory px-8 py-4 text-[11px] tracking-[0.25em] uppercase items-center gap-3 hover:bg-ink transition-colors duration-300">
              Sell Your Clothes <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", t: "Submit your clothes", d: "Photograph front, back and labels. Two minutes on your phone." },
    { n: "02", t: "We evaluate", d: "Brand, condition, age, demand and resale potential — reviewed by our team." },
    { n: "03", t: "Reusable → Cash", d: "Strong resale pieces earn a cash offer, paid after pickup inspection." },
    { n: "04", t: "Good → Cloth Cash", d: "Good pieces earn Cloth Cash for eligible JIGEL and AVAGINJA purchases." },
    { n: "05", t: "Recovery → Recovery Cloth Cash", d: "Well-worn items can still earn a small Cloth Cash thank-you." },
  ];
  return (
    <section className="bg-sand/60" data-testid="how-it-works">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-14 md:py-24">
        <Reveal><Overline>How It Works</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Five simple steps.</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08} className="bg-ivory p-7">
              <span className="font-serif text-4xl text-wine/25">{s.n}</span>
              <h3 className="font-serif text-xl mt-3">{s.t}</h3>
              <p className="mt-2 text-[13px] text-ink/55 leading-relaxed">{s.d}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8 border border-border bg-ivory p-6 max-w-3xl">
          <p className="text-sm text-ink/60 leading-relaxed">
            Not every item we receive becomes a JIGEL product. Only items that meet our resale standards are offered to another customer. Recovery items remain outside our resale collection.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ClothCashCross() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  return (
    <section className="bg-ink text-ivory" data-testid="cloth-cash-cross">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16 md:py-28 text-center">
        <Reveal>
          <Overline light>Cloth Cash</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">Use your Cloth Cash <span className="italic font-light text-gold">beyond JIGEL.</span></h2>
          <p className="mt-6 max-w-xl mx-auto text-ivory/60 text-base leading-relaxed">
            Your eligible Cloth Cash can be used toward purchases on JIGEL and AVAGINJA — one reward, two stores.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/shop" data-testid="shop-jigel-btn"
            className="bg-gold text-ink px-9 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-ivory transition-colors duration-300">Shop JIGEL</Link>
          <a href={settings?.avaginja_url || "https://avaginja.com"} target="_blank" rel="noreferrer" data-testid="shop-avaginja-btn"
            className="border border-ivory/40 text-ivory px-9 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-ivory hover:text-ink transition-colors duration-300 flex items-center gap-2">
            Shop AVAGINJA <ArrowUpRight size={14} strokeWidth={1.5} />
          </a>
        </Reveal>
        {settings && (
          <Reveal delay={0.2}>
            <p className="mt-8 text-[11px] tracking-[0.15em] uppercase text-ivory/40">
              Cloth Cash covers up to {settings.redemption_rules.max_percent}% of eligible orders · min order ₹{settings.redemption_rules.min_order} · max ₹{settings.redemption_rules.max_per_order} per order
            </p>
          </Reveal>
        )}
      </div>
    </section>
  );
}

const REVIEWS = [
  { name: "Ananya S.", text: "Sold three old jackets and got Cloth Cash the same week. Used it on a beautiful pre-owned blazer — the grading was exactly as described." },
  { name: "Rohit M.", text: "Finally a place that buys old clothes seriously. My Levi's got a cash offer, pickup was on time, and payment came right after inspection." },
  { name: "Priya K.", text: "Love that my Cloth Cash worked on AVAGINJA too. Sold clothes, bought groceries. Strange and wonderful." },
];

function Reviews() {
  return (
    <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-14 md:py-20" data-testid="reviews">
      <Reveal><Overline>Customer Reviews</Overline>
        <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Word of mouth.</h2>
      </Reveal>
      <div className="mt-10 grid md:grid-cols-3 gap-5">
        {REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.1} className="border border-border p-7">
            <div className="flex gap-1 text-gold">{[...Array(5)].map((_, j) => <Star key={j} size={13} className="fill-gold" />)}</div>
            <p className="mt-4 text-sm text-ink/65 leading-relaxed">“{r.text}”</p>
            <p className="mt-4 text-[11px] tracking-[0.2em] uppercase text-muted-foreground">{r.name}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const FAQS = [
  { q: "How do I sell my clothes to JIGEL?", a: "Photograph your item (front, back, labels), tell us the brand, age and condition, and submit. Our team reviews every submission and sends you a Cash or Cloth Cash offer." },
  { q: "What's the difference between Reusable, Good and Recovery?", a: "Reusable items have strong resale potential and earn a cash offer. Good items earn Cloth Cash. Very worn items can join Recovery for a small Cloth Cash thank-you. Items we can't responsibly accept are respectfully declined." },
  { q: "Where can I use Cloth Cash?", a: "On eligible purchases at JIGEL and at AVAGINJA.com. Cloth Cash is store credit — it isn't cash, can't be withdrawn, and follows the redemption rules shown on the Cloth Cash page." },
  { q: "Do all submitted clothes get sold on JIGEL?", a: "No. Only items that meet our resale standards are listed. Recovery items never enter the JIGEL catalog." },
  { q: "What does JIGEL Grade mean?", a: "Every pre-owned piece is graded A (like new / excellent), B (very good) or C (good) after inspection, so you know exactly what you're buying." },
];

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="bg-sand/60" data-testid="faq">
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-14 md:py-20">
        <Reveal className="text-center"><Overline>FAQ</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Good questions.</h2>
        </Reveal>
        <div className="mt-10 border-t border-border">
          {FAQS.map((f, i) => (
            <div key={i} className="border-b border-border">
              <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-${i}`}
                className="w-full flex items-center justify-between py-5 text-left gap-4">
                <span className="font-serif text-xl">{f.q}</span>
                <ChevronDown size={16} strokeWidth={1.5} className={`shrink-0 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <p className="pb-5 text-sm text-ink/60 leading-relaxed max-w-2xl">{f.a}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [brands, setBrands] = useState([]);
  const [sections, setSections] = useState(null);
  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSections(r.data.home_sections)).catch(() => setSections({}));
  }, []);
  const on = (k) => !sections || sections[k] !== false;
  return (
    <div data-testid="home-page">
      <Hero />
      <ModelCards />
      {on("shop_by_category") && <Departments />}
      {on("featured_brands") && <BrandTiles brands={brands} />}
      {on("featured") && <ProductRail overline="Trending Now" title="Featured pieces." params={{ pinned: true }} to="/shop" />}
      {on("new_arrivals") && <ProductRail overline="New Arrivals" title="Just landed." params={{ new_arrivals: true }} to="/new-arrivals" />}
      {on("trade_in_cta") && <SellSection />}
      <HowItWorks />
      {on("promise") && <ClothCashCross />}
      {on("preloved") && <ProductRail overline="Second-Life Collection" title="Pre-owned. JIGEL graded." params={{ preloved: true }} to="/pre-loved" />}
      {on("editors_picks") && <ProductRail overline="Editor's Picks" title="Chosen by our editors." params={{ editors_pick: true }} to="/shop" />}
      {on("why") && <Reviews />}
      <Faq />
    </div>
  );
}
